import { FC, useEffect, useState, useRef, useMemo, useCallback } from "react";
import {
  Box,
  CircularProgress,
  Typography,
  Alert,
  useTheme
} from "@mui/material";
import { FormikHelpers } from "formik";
import ReportsFilterBar from "components/common/reports/ReportFiltersBar/ReportsFilterBar";
import ReportsTopbar from "components/common/reports/ReportsTopbar/ReportsTopbar";
import WorkingHoursFilterForm from "components/forms/Reports/WorkingHoursFilterForm";

import { GraphicalWorkingHoursReport } from "@vilocnv/allsetra-core";

// Data
import { useAppDispatch, useAppSelector, useReportCommonStates } from "hooks";
import { selectAccountKeysState } from "app/data/selectors";
import {
  generateReportThunk,
  getAllKeysByAccountThunk,
  setReportsFilterData,
  setReportsDateValues
} from "app/features";
import {
  convertToNormalTime,
  formattedWorkingHours,
  reportTransformTimeForAPI,
  getStandardizedReportDates
} from "app/data/helpers";
import { WORKING_HOURS_HEADERS } from "app/data/constants";
import { useTranslation } from "react-i18next";
import { CenterTextBox } from "./Reports.styled";
import { restructureWorkingHoursData } from "app/data/helpers/ReportHelpers/graphicalReportHelpers";
import ChartsAccordion from "components/reports/ChartsAccordion/ChartsAccordion";

const DEFAULT_FILTER_VALUES = {
  key: [],
  group: [],
  object: [],
  objectType: []
};

const GraphicalWorkingHours: FC = () => {
  const theme = useTheme();
  const dispatch = useAppDispatch();

  const {
    drawerSelectedAccountId,
    objectTypes,
    objectTypesLoading,
    objectsLoading,
    accountGroups,
    report,
    reportsLoading,
    reportsHasExceededLimit,
    filterOpen,
    setFilterOpen,
    dateFormat,
    setDateFormat,
    dateValues,
    setDateValues,
    localReport,
    setLocalReport,
    handleObjectsSearch,
    selectedLocalObjects,
    handleOnChangeReportFilterForm,
    preloadedObjects
  } = useReportCommonStates();

  const [filterValues, setFilterValues] = useState<any>(DEFAULT_FILTER_VALUES);
  const [openModal, setOpenModal] = useState(false);
  const [apiPayload, setApiPayload] = useState(null);
  const apiPayloadRef = useRef(apiPayload);
  const [loadingElapsedSeconds, setLoadingElapsedSeconds] = useState(0);

  const [expandedFirstLevel, setExpandedFirstLevel] = useState<string[]>([]);
  const [expandedSecondLevel, setExpandedSecondLevel] = useState<string[]>([]);

  const { t, i18n } = useTranslation([
    "translation",
    "tableHeadingsTranslation"
  ]);

  const { accountKeys, loading: accountKeysLoading } = useAppSelector(
    selectAccountKeysState
  );

  const formattedData = useMemo(
    () => formattedWorkingHours(localReport, dateFormat),
    [localReport, dateFormat]
  );

  const restructuredData = useMemo(
    () => restructureWorkingHoursData(formattedData),
    [formattedData]
  );

  useEffect(() => {
    apiPayloadRef.current = apiPayload;
  }, [apiPayload]);

  const workingHoursFiltersSubmitHandler = async (
    values: any,
    formikHelpers: FormikHelpers<any>
  ) => {
    formikHelpers.setSubmitting(true);

    const endDate = new Date(dateValues.endDate);
    const startDate = new Date(dateValues.startDate);

    const sameDate = startDate.toDateString() === endDate.toDateString();

    if (sameDate) {
      endDate.setHours(23, 59, 59);
      startDate.setHours(0, 0, 0);
    }

    const formattedEndDate = convertToNormalTime(endDate, true);
    const formattedStartDate = convertToNormalTime(startDate);
    setFilterValues(values);

    const payload = {
      ...(apiPayloadRef.current ?? {}),
      startDate: formattedStartDate,
      endDate: formattedEndDate,
      ...values,
      // Empty array = all weekdays (same as WorkingHours.tsx / other report payloads to POST /reports).
      daysOfWeek:
        values.daysOfWeek.length === 7 ? [] : values.daysOfWeek.map(Number),
      startTime: reportTransformTimeForAPI(
        values.startTime + ":00",
        startDate,
        false
      ),
      endTime: reportTransformTimeForAPI(
        values.endTime + ":00",
        endDate,
        true,
        startDate
      ),
      reportType: 3
    };

    dispatch(setReportsFilterData(payload));
    apiPayloadRef.current = payload;
    setApiPayload(payload);

    formikHelpers.setSubmitting(false);
    setFilterOpen(false);
  };

  const handleDateChange = (value: any) => {
    const formattedValue = getStandardizedReportDates(value);
    setDateValues(formattedValue);
    dispatch(setReportsDateValues(formattedValue));
  };

  const handleDateRangeSubmit = useCallback(() => {
    const startDate = convertToNormalTime(dateValues.startDate);
    const endDate = convertToNormalTime(dateValues.endDate, true);

    let updatedFilterValues = { ...filterValues };
    if (filterValues.daysOfWeek) {
      updatedFilterValues = {
        ...filterValues,
        // Empty array = all weekdays (API contract aligned with WorkingHours and other reports).
        daysOfWeek:
          filterValues.daysOfWeek.length === 7
            ? []
            : filterValues.daysOfWeek.map(Number),
        startTime: reportTransformTimeForAPI(
          filterValues.startTime + ":00",
          dateValues.startDate,
          false
        ),
        endTime: reportTransformTimeForAPI(
          filterValues.endTime + ":00",
          dateValues.endDate,
          true,
          new Date(dateValues.startDate)
        )
      };
    }

    const payload = {
      ...(apiPayloadRef.current ?? {}),
      startDate,
      endDate,
      ...updatedFilterValues,
      reportType: 3
    };

    apiPayloadRef.current = payload;
    setApiPayload(payload);
  }, [dateValues, filterValues]);

  const handleLoadReport = () => {
    const payload = apiPayloadRef.current;
    if (payload == null) {
      return;
    }
    dispatch(setReportsFilterData(payload));
    dispatch(generateReportThunk(payload));
  };

  const formObjects = [...selectedLocalObjects];

  const handleFirstLevelAccordionChange = useCallback(
    (event: React.SyntheticEvent, expanded: boolean) => {
      const panel = (event.currentTarget as HTMLElement).getAttribute(
        "data-panel"
      );
      if (!panel) {
        return;
      }
      setExpandedFirstLevel((prev) =>
        expanded ? [...prev, panel] : prev.filter((p) => p !== panel)
      );
    },
    []
  );

  const handleSecondLevelAccordionChange = useCallback(
    (event: React.SyntheticEvent, expanded: boolean) => {
      const panel = (event.currentTarget as HTMLElement).getAttribute(
        "data-panel"
      );
      if (!panel) {
        return;
      }
      setExpandedSecondLevel((prev) =>
        expanded ? [...prev, panel] : prev.filter((p) => p !== panel)
      );
    },
    []
  );

  const workingHoursDataBars = useMemo(
    () => [
      {
        barKey: "ignitionHours",
        barColor: "rgba(218, 133, 204, 1)"
      },
      {
        barKey: "workingHours",
        barColor: "rgba(95, 159, 255, 1)"
      },
      {
        barKey: "ptoHours",
        barColor: "rgba(132, 90, 252, 1)"
      }
    ],
    []
  );

  const axisLabels = useMemo(
    () => ({
      x: t("reports.hours"),
      y: t("reports.minutes")
    }),
    [t, i18n.language]
  );

  const renderWorkingHoursChart = useCallback(
    (chartItem: any, width: number) => (
      <GraphicalWorkingHoursReport
        width={width}
        height={240}
        data={chartItem}
        barSize={10}
        xAxisLabel={axisLabels.x}
        yAxisLabel={axisLabels.y}
        dataBars={workingHoursDataBars}
      />
    ),
    [axisLabels.x, axisLabels.y, workingHoursDataBars]
  );

  useEffect(() => {
    handleDateRangeSubmit();
  }, [dateValues, handleDateRangeSubmit]);

  useEffect(() => {
    if (!reportsLoading) {
      setLoadingElapsedSeconds(0);
      return;
    }

    setLoadingElapsedSeconds(0);
    const id = window.setInterval(() => {
      setLoadingElapsedSeconds((s) => s + 1);
    }, 1000);

    return () => window.clearInterval(id);
  }, [reportsLoading]);

  useEffect(() => {
    setLocalReport(report);
  }, [report]);

  useEffect(() => {
    if (drawerSelectedAccountId !== null) {
      dispatch(getAllKeysByAccountThunk(drawerSelectedAccountId));
    }
  }, [drawerSelectedAccountId, dispatch]);

  return (
    <Box>
      <ReportsTopbar
        dropdownTitle={t("reports.graphicalWorkingHours")}
        disabled={reportsLoading ? true : false}
      />
      <Box mx={4}>
        <ReportsFilterBar
          setFilterOpen={setFilterOpen}
          dateFormat={dateFormat}
          setDateFormat={setDateFormat}
          handleDateChange={handleDateChange}
          dateValues={dateValues}
          handleDateRangeSubmit={handleDateRangeSubmit}
          dateRangeLoading={reportsLoading}
          exportHeaders={WORKING_HOURS_HEADERS}
          formattedData={formattedData}
          hideExportButton={true}
          disableExportButton={restructuredData?.length > 0 ? false : true}
          reportType={3}
          reportExportTypeValue={3}
          openModal={openModal}
          setOpenModal={setOpenModal}
          exportModalButton={true}
          handleLoadReport={handleLoadReport}
        />
        <WorkingHoursFilterForm
          fetchOnDebounce={handleObjectsSearch}
          open={filterOpen}
          onClose={() => setFilterOpen(false)}
          onSubmit={workingHoursFiltersSubmitHandler}
          groups={accountGroups}
          objectTypes={objectTypes}
          objects={formObjects.length ? formObjects : preloadedObjects}
          keys={accountKeys}
          dataLoading={
            objectsLoading || objectTypesLoading || accountKeysLoading
          }
          theme={theme}
          //@ts-ignore
          onChange={handleOnChangeReportFilterForm}
        />
        {reportsLoading && (
          <CenterTextBox
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: 2,
              py: 4
            }}
          >
            <CircularProgress />
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ maxWidth: 420 }}
            >
              {t("reports.graphicalWorkingHoursLoadingHint")}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {t("reports.graphicalWorkingHoursElapsed", {
                seconds: loadingElapsedSeconds
              })}
            </Typography>
          </CenterTextBox>
        )}
        {!reportsLoading &&
          reportsHasExceededLimit &&
          !!restructuredData?.length && (
            <Alert severity="warning" sx={{ my: 2 }}>
              {t("reports.workingHoursReportTruncated")}
            </Alert>
          )}
        {!reportsLoading && restructuredData?.length ? (
          <ChartsAccordion
            expandedFirstLevel={expandedFirstLevel}
            expandedSecondLevel={expandedSecondLevel}
            chartID="g-wh-report"
            data={restructuredData}
            handleFirstLevelAccordionChange={handleFirstLevelAccordionChange}
            handleSecondLevelAccordionChange={handleSecondLevelAccordionChange}
            renderGraphComponent={renderWorkingHoursChart}
          />
        ) : (
          !reportsLoading && (
            <CenterTextBox>
              {t("reports.graphicalWorkingHoursNoRecords")}
            </CenterTextBox>
          )
        )}
      </Box>
    </Box>
  );
};

export default GraphicalWorkingHours;
